import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Flex, Text, toast, Breadcrumbs } from "@jsq/ds/core";
import { FormTextInput, SubmitButton } from "@jsq/ds/forms";
import { useRecreatOutgoingPaymentMutation } from "~/graphql/supergraphHooks";
import { type RecreateOutgoingPaymentInput } from "~/graphql/supergraphTypes";
import { SPACING } from "~/styles/variables";
import * as styles from "./styles_vanilla.css";

type FormValues = {
  externalPaymentId: string;
  externalPaymentBatchId: string;
};

const validationSchema = Yup.object()
  .shape({
    externalPaymentId: Yup.string(),
    externalPaymentBatchId: Yup.string(),
  })
  .test("at-least-one-required", "At least one field is required.", function (values) {
    const hasPaymentId = values.externalPaymentId && values.externalPaymentId.trim() !== "";
    const hasBatchId =
      values.externalPaymentBatchId && values.externalPaymentBatchId.trim() !== "";

    // At least one field must be filled
    if (!(hasPaymentId || hasBatchId)) {
      return new Yup.ValidationError([
        this.createError({
          path: "externalPaymentId",
          message: "At least one field is required",
        }),
        this.createError({
          path: "externalPaymentBatchId",
          message: "At least one field is required",
        }),
      ]);
    }

    // Both fields can't be filled
    if (hasPaymentId && hasBatchId) {
      return new Yup.ValidationError([
        this.createError({
          path: "externalPaymentId",
          message: "Only one field may be provided.",
        }),
        this.createError({
          path: "externalPaymentBatchId",
          message: "Only one field may be provided.",
        }),
      ]);
    }

    return true;
  });

const initialValues: FormValues = {
  externalPaymentId: "",
  externalPaymentBatchId: "",
};

export const RecreateOutgoingPayment: React.FC = () => {
  const [recreateOutgoingPayment, { loading }] = useRecreatOutgoingPaymentMutation({
    onCompleted: (data) => {
      if (data?.recreateOutgoingPayment?.id) {
        toast.success("Outgoing payment recreated successfully");
      }
    },
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const input: RecreateOutgoingPaymentInput = {};

      if (values.externalPaymentId.trim()) {
        input.externalPaymentId = values.externalPaymentId.trim();
      }
      if (values.externalPaymentBatchId.trim()) {
        input.externalPaymentBatchId = values.externalPaymentBatchId.trim();
      }

      await recreateOutgoingPayment({
        variables: { recreateOutgoingPaymentInput: input },
      });
    } catch {
      toast.error(
        "The outgoing payment could not be recreated. Please check the input and try again.",
      );
    }
  };

  return (
    <Flex
      data-testid="recreate-outgoing-payment-root"
      flexDirection="column"
      className={styles.container}
    >
      <div className={styles.header}>
        <div className={styles.breadcrumbsContainer}>
          <Breadcrumbs
            breadcrumbs={[
              {
                path: "/treasury/configuration",
                name: "Configuration",
                icon: "cog",
              },
              {
                name: "Recreate outgoing payment",
              },
            ]}
          />
        </div>
        <Text variant="regular-xxxl" className={styles.pageTitle}>
          Recreate outgoing payment
        </Text>
        <Text variant="regular-md" className={styles.pageSubtitle}>
          This will only send out any PENDING payments. Any payment that is already transmitted
          or failed/canceled will not be resent
        </Text>
      </div>
      <div className={styles.contentArea} data-testid="recreate-outgoing-payment-content">
        <div className={styles.formSection}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              <Flex flexDirection="column" gap={SPACING.MEDIUM} className={styles.formFields}>
                <div className={styles.formField}>
                  <FormTextInput
                    name="externalPaymentId"
                    label="External Payment ID"
                    placeholder="Enter external payment ID"
                    helpText="Only provide a failed payment."
                    isFullWidth
                  />
                </div>
                <div className={styles.formField}>
                  <FormTextInput
                    name="externalPaymentBatchId"
                    label="External Payment Batch ID"
                    placeholder="Enter external payment batch ID"
                    isFullWidth
                  />
                </div>
              </Flex>
              <Flex justifyContent="flex-start">
                <SubmitButton disabled={loading} data-testid="submit-button">
                  Recreate outgoing payment
                </SubmitButton>
              </Flex>
            </Form>
          </Formik>
        </div>
      </div>
    </Flex>
  );
};

export default RecreateOutgoingPayment;
